import { useState } from "react"
import { Accordion, CheckboxButton, DatePickerCustom, InputNumber, InputSubTasa, InputTasa, Modal, TableCustom, FormError, FormLabel, CheckboxButtonGroup } from "../../../components/common"
import { ALERT_TYPE, OPERATION_MODE, ShowToastMessage } from '@laramie/ui';
import { useEntidad } from "../../../components/hooks/useEntidad"
import { useFormErrors } from "../../../components/hooks/useFormErrors"
import { getBooleanToString, getDateNow, getDateToString, getFormatNumber } from "../../../utils/convert"
import { isNull, isValidInteger } from "../../../utils/validator"
import { VALIDATION_MESSAGE } from "../../../consts/validationMessage"
import {TIPS} from "../../../consts/tips";

const Conceptos = ({ params, idTipoTributo, aplicaValorUF, conceptos, onAddConcepto, onModifyConcepto, onRemoveConcepto }) => {
    const [editItem, setEditItem] = useState(null)
    const [editMode, setEditMode] = useState(null)

    const [, getEntidad, entidadesReady] = useEntidad({
        entidades: ['Tasa', 'SubTasa'],
        onLoaded: (entidades, isSuccess, error) => {
          if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        }
    })

    const getCell = (entidad, value) => {
        if (!entidadesReady) return ''
        else {
            const row = getEntidad(entidad, value)
            if (row) {
                const { codigo, descripcion } = getEntidad(entidad, value)
                return `${codigo} - ${descripcion}`
            }
            else return ""
        }
    }

    const validateDateRanges = (item) => {
        const listOthers = conceptos.filter(f => f.id !== item.id);
        const conflicts = listOthers.filter(f =>
            (f.idTasa === item.idTasa && f.idSubTasa === item.idSubTasa) &&
            (f.fechaDesde <= item.fechaHasta || isNull(f.fechaDesde) || isNull(item.fechaHasta)) &&
            (f.fechaHasta >= item.fechaDesde || isNull(f.fechaHasta) || isNull(item.fechaDesde))
        );
        return (conflicts.length === 0);
    }

    const getErrors = () => {

        const errors = {}

        if (!editItem) return errors

        if (editItem.idTasa <= 0) errors.idTasa = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (editItem.idSubTasa <= 0) errors.idSubTasa = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (aplicaValorUF) {
            if (!(editItem.multiplicadorUF > 0)) errors.multiplicadorUF = 'Ingresá un número mayor a cero'
        }
        else if (editItem.valor <= 0) errors.valor = 'Ingresá un número mayor a cero'

        if (editItem.admiteCantidad) {
            if (!isValidInteger(editItem.cantidadDesde, true)) errors.cantidadDesde = 'Ingresá un número mayor a cero'

            if (!isValidInteger(editItem.cantidadHasta, true)) errors.cantidadHasta = 'Ingresá un número mayor a cero'
            else if (editItem.cantidadDesde > editItem.cantidadHasta) errors.cantidadHasta = 'No puede ser menor a la cantidad desde'
        }

        if (isNull(editItem.fechaDesde)) errors.fechaDesde = VALIDATION_MESSAGE.REQUIRED_DATE
        else if (!validateDateRanges(editItem)) errors.fechaDesde = 'Se superpone con otro período'

        if (!isNull(editItem.fechaHasta) && editItem.fechaDesde > editItem.fechaHasta) errors.fechaHasta = 'No puede ser anterior a la fecha desde'

        return errors
    }

    const { errorOf, validate, reset } = useFormErrors(getErrors)

    const admiteOpcional = editItem !== null && (editItem.admiteCantidad || (!aplicaValorUF && editItem.importeEditable))

    const closeForm = () => {
        reset()
        setEditItem(null)
        setEditMode(null)
    }

    const onClickConfirm = () => {
        if (!validate()) return

        const item = {...editItem, opcional: admiteOpcional && !!editItem.opcional}
        if (editMode === OPERATION_MODE.NEW) onAddConcepto(item)
        else onModifyConcepto(item)

        closeForm()
    }

    const avmr = {
        onAdd: () => { // NOTE: webapi explota si se manda un parámetro inválido
            const today = getDateNow(false);
            setEditMode(OPERATION_MODE.NEW)
            setEditItem({
                id: -new Date(),
                idReciboEspecial: parseInt(params.id),
                idTasa: 0,
                idSubTasa: 0,
                valor: 0,
                multiplicadorUF: null,
                fechaDesde: today,
                fechaHasta: null,
                admiteCantidad: false,
                cantidadDesde: 0,
                cantidadHasta: 0,
                importeEditable: false,
                opcional: false,
                state: 'a',
            })
        },
        onModify: (item) => {
            setEditMode(OPERATION_MODE.EDIT)
            // Con el recibo en pesos, un concepto en módulos (al destildar unidades fijas) arranca sin valor: su número son módulos.
            const enModulos = !aplicaValorUF && !isNull(item.multiplicadorUF)
            setEditItem({...item, ...(enModulos ? { valor: 0, multiplicadorUF: null } : {}), state: item.state === 'a' ? 'a' : 'm'})
        },
        onRemove: (item) => {
            onRemoveConcepto(item)
        }
    }

    return <>
        
        <Accordion title="Conceptos" startOpen>
            <TableCustom
                className="TableCustomBase"
                data={conceptos}
                avmr={params.mode !== OPERATION_MODE.VIEW ? avmr : undefined}
                columns={[
                    { Header: 'Tasa', accessor: 'idTasa', Cell: ({ value }) => getCell('Tasa', value), width: '20%' },
                    { Header: 'Sub tasa', accessor: 'idSubTasa', Cell: ({ value }) => getCell('SubTasa', value), width: '20%' },
                    aplicaValorUF
                        ? { Header: 'Multiplicador UF', accessor: 'multiplicadorUF', Cell: ({ value }) => isNull(value) ? '' : getFormatNumber(value, 3), width: '10%', alignCell: 'right' }
                        : { Header: 'Valor', accessor: 'valor', Cell: ({ row }) => isNull(row.original.multiplicadorUF) ? getFormatNumber(row.original.valor, 2) : '', width: '10%', alignCell: 'right' },
                    { Header: 'Desde', Cell: (props) => getDateToString(props.value, false), accessor: 'fechaDesde', width: '10%' },
                    { Header: 'Hasta', Cell: (props) => getDateToString(props.value, false), accessor: 'fechaHasta', width: '10%' },
                    { Header: 'Cantidad', accessor: 'admiteCantidad', Cell: ({ row }) => !row.original.admiteCantidad ? 1 : row.original.cantidadDesde === row.original.cantidadHasta ? row.original.cantidadDesde : `${row.original.cantidadDesde} - ${row.original.cantidadHasta}`, width: '10%', alignCell: 'right' },
                    { Header: 'Importe editable', accessor: 'importeEditable', Cell: ({ value }) => getBooleanToString(value), width: '10%' },
                    { Header: 'Opcional', accessor: 'opcional', Cell: ({ value }) => getBooleanToString(value), width: '10%' }
                ]}
            />
        </Accordion>

        {editItem !== null && <Modal
            onEscape={closeForm}
            show
            header={<h2 className="modal-title">Definición de concepto</h2>}
            tipName={TIPS.NAME.MODALS.DEFINICION_CONCEPTO_MODAL}
            body={(
                <Accordion title="Datos del concepto" startOpen={true}>
                    <div className='row form-basic'>
                        <div className="mb-3 col-12 col-md-6">
                            <FormLabel htmlFor="idTasa" required>Tasa</FormLabel>
                            <InputTasa
                                name="idTasa"
                                placeholder=""
                                idTipoTributo={idTipoTributo}
                                className={`form-control ${errorOf('idTasa') ? 'is-invalid' : ''}`}
                                value={editItem.idTasa}
                                onChange={({ target }) => setEditItem(x => ({...x, idTasa: target.value}))}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                            />
                            <FormError message={errorOf('idTasa')} />
                        </div>
                        <div className="mb-3 col-12 col-md-6">
                            <FormLabel htmlFor="idSubTasa" required>Sub tasa</FormLabel>
                            <InputSubTasa
                                name="idSubTasa"
                                placeholder=""
                                className={`form-control ${errorOf('idSubTasa') ? 'is-invalid' : ''}`}
                                value={editItem.idSubTasa}
                                onChange={({ target }) => setEditItem(x => ({...x, idSubTasa: target.value}))}
                                idTasa={editItem.idTasa}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                            />
                            <FormError message={errorOf('idSubTasa')} />
                        </div>
                        {aplicaValorUF ?
                        <div className="mb-3 col-12 col-md-3">
                            <FormLabel htmlFor="multiplicadorUF" required>Multiplicador UF</FormLabel>
                            <InputNumber
                                type="number"
                                name="multiplicadorUF"
                                className={`form-control ${errorOf('multiplicadorUF') ? 'is-invalid' : ''}`}
                                precision={3}
                                value={editItem.multiplicadorUF ?? 0}
                                onChange={({ target }) => setEditItem(x => ({...x, multiplicadorUF: target.value}))}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                            />
                            <FormError message={errorOf('multiplicadorUF')} />
                        </div>
                        :
                        <div className="mb-3 col-12 col-md-3">
                            <FormLabel htmlFor="valor" required>Valor</FormLabel>
                            <InputNumber
                                type="number"
                                name="valor"
                                className={`form-control ${errorOf('valor') ? 'is-invalid' : ''}`}
                                precision={2}
                                value={editItem.valor}
                                onChange={({ target }) => setEditItem(x => ({...x, valor: target.value}))}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                            />
                            <FormError message={errorOf('valor')} />
                        </div>
                        }
                        <div className="mb-3 col-12 col-md-3">
                            <FormLabel htmlFor="fechaDesde" required>Fecha desde</FormLabel>
                            <DatePickerCustom
                                name="fechaDesde"
                                placeholder=""
                                className={`form-control ${errorOf('fechaDesde') ? 'is-invalid' : ''}`}
                                value={ editItem.fechaDesde }
                                onChange={({ target }) => setEditItem(x => ({...x, fechaDesde: target.value}))}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                            />
                            <FormError message={errorOf('fechaDesde')} />
                        </div>
                        <div className="mb-3 col-12 col-md-3">
                            <label htmlFor="fechaHasta" className="form-label">Fecha hasta</label>
                            <DatePickerCustom
                                name="fechaHasta"
                                placeholder=""
                                className={`form-control ${errorOf('fechaHasta') ? 'is-invalid' : ''}`}
                                value={ editItem.fechaHasta }
                                onChange={({ target }) => setEditItem(x => ({...x, fechaHasta: target.value}))}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                                minValue={editItem.fechaDesde}
                            />
                            <FormError message={errorOf('fechaHasta')} />
                        </div>
                        <div className="w-100"></div>
                        <div className="mb-3 col-12 col-lg-3">
                            <CheckboxButtonGroup label="Importe">
                                <CheckboxButton
                                    name="importeEditable"
                                    label="Admite edición"
                                    checked={!aplicaValorUF && editItem.importeEditable}
                                    onChange={({ target }) => setEditItem(x => ({...x, importeEditable: target.checked, opcional: (target.checked || x.admiteCantidad) ? x.opcional : false}))}
                                    disabled={params.mode === OPERATION_MODE.VIEW || aplicaValorUF}
                                />
                            </CheckboxButtonGroup>
                        </div>
                        <div className="mb-3 col-12 col-lg-3">
                            <CheckboxButtonGroup label="Cantidad">
                                <CheckboxButton
                                    name="admiteCantidad"
                                    label="Admite edición"
                                    checked={editItem.admiteCantidad}
                                    onChange={({ target }) => setEditItem(x => ({...x, admiteCantidad: target.checked, cantidadDesde: target.checked ? x.cantidadDesde : 0, cantidadHasta: target.checked ? x.cantidadHasta : 0, opcional: (target.checked || (!aplicaValorUF && x.importeEditable)) ? x.opcional : false}))}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                            </CheckboxButtonGroup>
                        </div>
                        <div className="mb-3 col-12 col-lg-3">
                            <CheckboxButtonGroup label="Opcional">
                                <CheckboxButton
                                    name="opcional"
                                    label="Admite cero"
                                    checked={admiteOpcional && editItem.opcional}
                                    onChange={({ target }) => setEditItem(x => ({...x, opcional: target.checked}))}
                                    disabled={params.mode === OPERATION_MODE.VIEW || !admiteOpcional}
                                />
                            </CheckboxButtonGroup>
                        </div>
                        {editItem.admiteCantidad &&
                        <>
                        <div className="w-100"></div>
                        <div className="mb-3 col-12 col-lg-3">
                            <FormLabel htmlFor="cantidadDesde" required>Desde</FormLabel>
                            <InputNumber
                                name="cantidadDesde"
                                className={`form-control ${errorOf('cantidadDesde') ? 'is-invalid' : ''}`}
                                precision={0}
                                value={editItem.cantidadDesde??0}
                                onChange={({ target }) => setEditItem(x => ({...x, cantidadDesde: target.value}))}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                            />
                            <FormError message={errorOf('cantidadDesde')} />
                        </div>
                        <div className="mb-3 col-12 col-lg-3">
                            <FormLabel htmlFor="cantidadHasta" required>Hasta</FormLabel>
                            <InputNumber
                                name="cantidadHasta"
                                className={`form-control ${errorOf('cantidadHasta') ? 'is-invalid' : ''}`}
                                precision={0}
                                value={editItem.cantidadHasta??0}
                                onChange={({ target }) => setEditItem(x => ({...x, cantidadHasta: target.value}))}
                                disabled={params.mode === OPERATION_MODE.VIEW}
                            />
                            <FormError message={errorOf('cantidadHasta')} />
                        </div>
                        </>
                        }
                    </div>
                </Accordion>
            )}
            footer={(
                <>
                <button
                    className="btn btn-outline-primary"
                    data-dismiss="modal"
                    onClick={closeForm}
                >Cancelar</button>
                {params.mode !== OPERATION_MODE.VIEW && (
                <button
                    className="btn btn-primary"
                    data-dismiss="modal"
                    onClick={onClickConfirm}
                >Aceptar</button>
                )}
                </>
            )}
        />}
    </>
}

export default Conceptos
